import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PaginationLoader,
	PopupSnackbar,
	SendEmailModal,
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import * as fromHealthSafetyForm from "features/health-safety-forms";
import { HOME_ROUTE } from "features/home/home-routes";
import SafetyMinutesCard from "../cards/safety-minutes-card";
import {
	EditSafetyMinutesForm,
	PageHeaderTabs,
} from "features/health-safety-forms";
import { getPageResults, useIsOnline, useTerminology } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { initialJobState } from "features/health-and-safety/reducers/health-safety-reducer";
import { CardText } from "features/timesheet/components/timesheet-list";
import InfiniteScroll from "react-infinite-scroll-component";

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: "none",
	},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const SafetyMinutesList = ({
	role,
	safetyMinutes,
	onDelete,
	onGetSafetyMinutesList,
	currentOrganisationGUID,
	addSafetyMinutesForm,
}) => {
	const isOnline = useIsOnline();
	const classes = useStyles();
	const { siteLabel } = useTerminology();
	const [showEditModal, setShowEditModal] = useState(false);
	const [showViewModal, setShowViewModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [selectedItemId, setSelectedItemId] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [siteId, setSiteId] = useState(null);
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [previousSaveSuccess, setPreviousSaveSuccess] = useState(false);
	const isInitialMount = useRef(true);

	const [safetyMinutesList, setSafetyMinutesList] = useState(getPageResults(initialJobState));
	const [currentPage, setCurrentPage] = useState(1);
	const defaultPageSize = 10; // Reduced from 20 to improve initial load performance
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
	const [hasMoreSafetyMinutes, setHasMoreSafetyMinutes] = useState(true);
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const ref = useRef(null);
	const [height, setHeight] = useState(0);

	const empty = safetyMinutesList && safetyMinutesList.length === 0;

	const onEdit = (id) => () => {
		setSelectedItemId(id);
		setShowEditModal(true);
	};
	const onView = (id) => () => {
		setSelectedItemId(id);
		setShowViewModal(true);
	};
	const onEmail = (id, siteId) => () => {
		setSelectedItemId(id);
		setSiteId(siteId);
		setShowEmailModal(true);
	};
	const handleApiError = (error) => {
		if (error.response && error.response.status === "401") {
			setErrorMessage("You are not authorized to perform this action.");
		} else if (error.response && error.response.status === "404") {
			setErrorMessage("The record no longer exists.");
		} else {
			setErrorMessage(
				"Something went wrong, please refresh the page and try again."
			);
		}
	};

	const isFetching = safetyMinutes.flag === "processing" && isOnline;
	// const isDoneFetching = safetyMinutes.flag === "done" || !isOnline;

	const setNewSafetyMinutesState = (safetyMinutesResult, page) => {
		let newState = initialJobState;

		if (safetyMinutesResult && safetyMinutesResult.results && safetyMinutesResult.results.length > 0) {
			newState = {
				...newState,
				...safetyMinutesResult
			};
		}

		newState.refreshList = safetyMinutesResult.refreshList;
		newState.flag = "done";
		// If navigating to first page clear the results from safetyMinutesList otherwise add to the end of it
		if (page === 1) {
			setSafetyMinutesList([...getPageResults(newState)]);
		} else {
			setSafetyMinutesList([...safetyMinutesList, ...getPageResults(newState)]);
		}
		setIsDoneFetching(true);
	};

	const getSafetyMinutes = (
		fromPageToRequest = currentPage, // fromPageToRequest is the page to request from the API,
		recordDeletedOrUpdated = false, // recordDeletedOrUpdated is a boolean to determine whether to overwrite or append to safetyMinutesLists
		pageSize = defaultPageSize // pageSize is the number of records to load from the API, can be paramaterised to request more data.
	) => {
		if (totalNumberOfRecords !== safetyMinutesList.length || recordDeletedOrUpdated) {
			if (recordDeletedOrUpdated) {
				setHasMoreSafetyMinutes(true);
			}

			safetyWiseApi
				.post(`${currentOrganisationGUID}/safety-minutes/paged`, {
					ascending: false,
					page: fromPageToRequest,
					pageSize,
					orderBy: "createdAt",
					searchKey: searchFilter
				})
				.then((result) => {
					setNewSafetyMinutesState(result.data, fromPageToRequest);

					// If more then the defaultPageSize data is requested from first page then dynamically set the current page, else set to fromPageToRequest
					if (pageSize > defaultPageSize && fromPageToRequest === 1) {
						setCurrentPage(pageSize / defaultPageSize);
					} else {
						setCurrentPage(fromPageToRequest);
					}

					setTotalNumberOfRecords(result.data.totalNumberOfRecords);
					if (
						result.data.totalNumberOfRecords ==
						safetyMinutesList.length + result.data.results.length
					) {
						setHasMoreSafetyMinutes(false);
					}
				})
				.catch((error) => {
					console.error("Error fetching safety minutes:", error);
					setErrorMessage("Failed to load safety minutes. Please try again.");
					setIsDoneFetching(true);
					setHasMoreSafetyMinutes(false);
				});
		} else {
			setHasMoreSafetyMinutes(false);
		}
	};

	// The InfiniteScroll Component requires a height for it to work  effectively and for it resize relative to the parent.
	useEffect(() => {
		const handleResize = () => {
			if (ref.current) {
				setHeight(ref.current.offsetHeight - 20); // reduce height by 20px otherwise the scrollbar flows past the height of screen.
			}
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Initial load - wait for currentOrganisationGUID to be available
	useEffect(() => {
		if (currentOrganisationGUID) {
			getSafetyMinutes();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganisationGUID]);

	// Watch for form save success and refresh list
	useEffect(() => {
		if (addSafetyMinutesForm?.save?.success && !previousSaveSuccess) {
			setPreviousSaveSuccess(true);
			// Refresh the list when a save succeeds
			if (selectedItemId) {
				const safetyMinutesIndex = safetyMinutesList.findIndex((t) => t.id === selectedItemId);
				const pageSizeToLoad =
					safetyMinutesIndex >= 0
						? Math.ceil((safetyMinutesIndex + 1) / defaultPageSize) * defaultPageSize
						: defaultPageSize;
				getSafetyMinutes(1, true, pageSizeToLoad);
			} else {
				getSafetyMinutes(1, true);
			}
		} else if (!addSafetyMinutesForm?.save?.success && previousSaveSuccess) {
			setPreviousSaveSuccess(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addSafetyMinutesForm?.save?.success, previousSaveSuccess]);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount - initial load is handled by the first useEffect
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Reset to first page when search changes
			setCurrentPage(1);
			setSafetyMinutesList([]);
			setTotalNumberOfRecords(1);
			setHasMoreSafetyMinutes(true);
			setIsDoneFetching(false);
			getSafetyMinutes(1, true);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter]);

	return (
		<Page className={classes.root} title="Safety Minutes" style={{ height: "100%" }}>
			<div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
				{/* <p>
					{totalNumberOfRecords} {safetyMinutesList.length}
				</p> */}
				{/* n.o of records and length of loaded list */}
				<InfiniteScroll
					dataLength={safetyMinutesList.length}
					next={() => {
						getSafetyMinutes(currentPage + 1);
					}}
					hasMore={hasMoreSafetyMinutes}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					<ScrollToTopOnMount />

											<PageHeaderTabs
							title="Safety Minutes"
							staffUserHide={false}
							addTabPath={fromHealthSafetyForm.ADD_SAFETY_MINUTES_ROUTE}
							hideListTabButton={true}
							backActionPath={HOME_ROUTE}
						
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
							{/* START: Search */}
							{showFilters && (
								<div className={classes.gridContainer}>
									<Grid container spacing={2} className={classes.filterGrid}>
										<Grid item xs={12} md={6}>
											<TextField
												className={classes.search}
												id="input-with-icon-textfield"
												variant="outlined"
												label="Search"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: searchFilter && (
														<InputAdornment position="end">
															<IconButton
																aria-label="clear search"
																onClick={() => {
																	setSearchFilter("");
																	setCurrentPage(1);
																	setSafetyMinutesList([]);
																	setTotalNumberOfRecords(1);
																	setHasMoreSafetyMinutes(true);
																	setIsDoneFetching(false);
																	getSafetyMinutes(1, true);
																}}
																edge="end"
																size="small"
															>
																<ClearIcon />
															</IconButton>
														</InputAdornment>
													)
												}}
												value={searchFilter}
												onChange={(val) => {
													const { value } = val.target;
													setSearchFilter(value);
													// Real-time search with debounce effect will handle the API call
												}}
												style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
												placeholder={`e.g. Title, ${siteLabel} Name`}
											/>
										</Grid>
									</Grid>
								</div>
							)}
							{/* END: Search */}
						</PageHeaderTabs>
					<PageContent pad>
						{/* <PaginationHeader pagination={safetyMinutes} /> */}
						<p style={{ marginTop: 5 }}>
							{safetyMinutesList.length > 0 ? totalNumberOfRecords + " records" : "No records loaded"}
						</p>

						{!empty &&
							isDoneFetching &&
							safetyMinutesList.map((safetyMinutes, index) => (
								<SafetyMinutesCard
									key={index}
									role={role}
									safetyMinutes={safetyMinutes}
									onEdit={onEdit(safetyMinutes.id)}
									onView={onView(safetyMinutes.id)}
									onDelete={() => {
										//index + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
										const pageSizeToLoad =
										Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;

										safetyWiseApi
											.del(`${currentOrganisationGUID}/safety-minutes/delete/${safetyMinutes.id}`)
											.then(() => {
												getSafetyMinutes(1, true, pageSizeToLoad);
											})
											.catch((error) => {
												console.error("Error deleting safety minutes:", error);
												handleApiError(error);
											});
									}}
									onEmail={onEmail(
										safetyMinutes.id,
										safetyMinutes.siteId
									)}
								/>
							))}

						{isFetching && <PaginationLoader height={90} pagination={safetyMinutes} />}

						{empty && isDoneFetching && (
							<Typography variant="body1">
								There are currently no Safety Minutes listed.
							</Typography>
						)}

						{/* <Pagination
							onGetList={onGetSafetyMinutesList}
							pagination={safetyMinutes}
							orderBy="createdAt"
							ascending={false}
							searchKey={searchFilter}
						/> */}
					</PageContent>
					{showEditModal && (
						<EditSafetyMinutesForm
							id={selectedItemId}
							onClose={(submitted = false) => {
								setShowEditModal(false);
								if (submitted) {
									const safetyMinutesIndex = safetyMinutesList.findIndex((t) => t.id === selectedItemId);
									const pageSizeToLoad =
										safetyMinutesIndex >= 0
											? Math.ceil((safetyMinutesIndex + 1) / defaultPageSize) * defaultPageSize
											: defaultPageSize;
									getSafetyMinutes(1, true, pageSizeToLoad);
								}
							}}
						/>
					)}
					{showViewModal && (
						<EditSafetyMinutesForm
							readOnly
							id={selectedItemId}
							onClose={() => setShowViewModal(false)}
						/>
					)}
			<SendEmailModal
				isOpen={showEmailModal}
				title={"Email Safety Minute Record"}
				onClose={() => setShowEmailModal(false)}
				onSend={(selectedRecipients) => {
					setIsSendingEmail(true);
					safetyWiseApi
						.post(
							`${currentOrganisationGUID}/safety-minutes/email/${selectedItemId}`,
							{
								toInternalAddresses:
									selectedRecipients.recipients,
								toExternalAddresses: [
									...selectedRecipients.recipientsOther,
									...selectedRecipients.recipientsCustomerSites,
								],
							}
						)
						.then(() => {
							setSuccessMessage("Emails sent successfully.");
							const loadedCount = safetyMinutesList.length;
							getSafetyMinutes(1, true, loadedCount > defaultPageSize ? loadedCount : defaultPageSize);
						})
						.catch(handleApiError)
						.finally(() => {
							setIsSendingEmail(false);
							setShowEmailModal(false);
						});
				}}
				isSaving={isSendingEmail}
				selectedItemId={selectedItemId}
				siteId={siteId}
				emailListType={"relatedToParticularSite"}
			/>
					<PopupSnackbar
						message={errorMessage}
						coloured
						error
						onExited={() => setErrorMessage("")}
					/>
					<PopupSnackbar
						message={successMessage}
						coloured
						success
						onExited={() => setSuccessMessage("")}
					/>
					{!isDoneFetching && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
					{!hasMoreSafetyMinutes && <CardText message={"All safety minutes have been loaded."} />}
					{safetyMinutesList.length === 0 && isDoneFetching && (
						<CardText message={"No safety minutes to show."} />
					)}
				</InfiniteScroll>
			</div>
		</Page>
	);
};

SafetyMinutesList.propTypes = {
	role: PropTypes.string.isRequired,
	safetyMinutes: PropTypes.object.isRequired,
	onDelete: PropTypes.func.isRequired,
	onGetSafetyMinutesList: PropTypes.func.isRequired,
	currentOrganisationGUID: PropTypes.string,
	addSafetyMinutesForm: PropTypes.object,
};

export default SafetyMinutesList;
